import React, { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import ReservationFloorPlan from "./ReservationFloorPlan.js";
import ReservationCard from "../components/ReservationCard.js";
import { api } from "../App";
import ReservationTimeCenterScroll from "./ReservationTimeCenterScroll.js";

const BoardWrap = styled.div`
  display: grid;
  grid-template-columns: 360px minmax(0, 1fr);
  gap: 0;
  margin: 0;
  padding: 0;
  background: transparent;
  border: none;
  border-radius: 0;
  height: 100%;
  min-height: 0;
  overflow: hidden;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
    overflow: auto;
  }
`;

const LeftPanel = styled.div`
  height: 100%;
  min-height: 0;
  background: #ffffff;
  border: none;
  border-right: 1px solid #eadfd5;
  border-radius: 0;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 900px) {
    border-right: none;
    border-bottom: 1px solid #eadfd5;
    min-height: 420px;
  }
`;

const LeftControls = styled.div`
  padding: 8px;
  margin-bottom: 0;
  border-bottom: 1px solid #efe6dc;
  background: #ffffff;

  & > * {
    margin: 0 !important;
    width: 100%;
  }
`;

const RightPanel = styled.div`
  height: 100%;
  min-height: 0;
  min-width: 0;
  background: #ffffff;
  border: none;
  border-radius: 0;
  padding: 0 0 0 12px;
  display: flex;
  flex-direction: column;
  overflow: hidden;

  @media (max-width: 900px) {
    padding: 10px 0 0 0;
    min-height: 420px;
  }
`;

const FloorHeader = styled.div`
  display: grid;
  grid-template-columns: minmax(120px, 1fr) minmax(180px, 360px) minmax(
      120px,
      1fr
    );
  align-items: center;
  gap: 12px;
  padding: 0 0 8px 0;
  margin-bottom: 8px;
  border-bottom: 1px solid #efe6dc;

  @media (max-width: 900px) {
    grid-template-columns: 1fr;
  }
`;

const FloorHeaderSpacer = styled.div`
  min-width: 0;

  @media (max-width: 900px) {
    display: none;
  }
`;

const FloorHeaderTimeCenter = styled.div`
  min-width: 0;
  width: 100%;
`;

const FloorPlanArea = styled.div`
  min-height: 0;
  flex: 1;
  display: flex;
  flex-direction: column;
  opacity: ${({ $isSwitching }) => ($isSwitching ? 0 : 1)};
  pointer-events: ${({ $isSwitching }) => ($isSwitching ? "none" : "auto")};
  transition: opacity 0.08s ease;
`;

const ZonePickerBox = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  flex-wrap: wrap;
  margin-bottom: 8px;
`;

const ZonePickerLabel = styled.div`
  font-size: 0.78rem;
  font-weight: 800;
  color: #6d625d;
`;

const ZoneBubble = styled.button`
  border: 1px solid ${({ active }) => (active ? "#EE754C" : "#e2d8cf")};
  background: ${({ active }) => (active ? "#fff1e8" : "#ffffff")};
  color: ${({ active }) => (active ? "#EE754C" : "#6d625d")};
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 0.82rem;
  font-weight: 750;
  cursor: pointer;
  transition: all 0.15s ease;
  box-shadow: ${({ active }) =>
    active ? "0 3px 8px rgba(238, 117, 76, 0.12)" : "none"};

  &:hover {
    border-color: #ee754c;
    background: #fff7f2;
  }
`;

const ZoneSelect = styled.select`
  border: 1px solid #e2d8cf;
  border-radius: 9px;
  padding: 7px 10px;
  font-size: 0.88rem;
  color: #4b3335;
  background: #fff;
`;

const EmptyState = styled.div`
  padding: 22px;
  color: #7a6d68;
  text-align: center;
  border: 1px dashed #e3d8cf;
  background: #fffaf4;
  font-weight: 650;
`;

const Legend = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  font-size: 0.76rem;
  color: #6d625d;
  margin-top: 8px;
  padding: 0;
`;

const LegendDot = styled.span`
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 4px;
  margin-right: 5px;
  vertical-align: -2px;
  background: ${({ color }) => color};
  border: 1px solid #cbbfb5;
`;

const ReservationList = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding: 0;

  scrollbar-width: thin;
  scrollbar-color: rgba(153, 176, 173, 0.38) transparent;

  &::-webkit-scrollbar {
    width: 3px;
    height: 3px;
  }

  &::-webkit-scrollbar-track {
    background: transparent;
  }

  &::-webkit-scrollbar-thumb {
    background-color: rgba(153, 176, 173, 0.38);
    border-radius: 999px;
  }

  &::-webkit-scrollbar-thumb:hover {
    background-color: rgba(153, 176, 173, 0.65);
  }

  &::-webkit-scrollbar-button {
    display: none;
    width: 0;
    height: 0;
  }
`;

const FloorHeaderActions = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 6px;
  flex-wrap: wrap;
  min-width: 0;

  @media (max-width: 900px) {
    justify-content: flex-start;
  }
`;

const FloorToolButton = styled.button`
  height: 30px;
  border-radius: 7px;
  border: 1px solid ${({ active }) => (active ? "#EE754C" : "#ddd")};
  background: ${({ active }) => (active ? "#fff1e8" : "#fff")};
  color: ${({ active }) => (active ? "#EE754C" : "#4b3335")};
  padding: 0 10px;
  font-size: 0.78rem;
  font-weight: 800;
  cursor: pointer;

  &:hover {
    border-color: #ee754c;
    background: #fff7f2;
  }

  &:disabled {
    opacity: 0.55;
    cursor: not-allowed;
  }
`;

function normalizeDateForUrl(date) {
  if (!date) return "";

  if (typeof date === "string" && /^\d{4}-\d{2}-\d{2}/.test(date)) {
    return date.slice(0, 10);
  }

  const parsedDate = date instanceof Date ? date : new Date(date);

  if (Number.isNaN(parsedDate.getTime())) {
    return "";
  }

  const year = parsedDate.getFullYear();
  const month = String(parsedDate.getMonth() + 1).padStart(2, "0");
  const day = String(parsedDate.getDate()).padStart(2, "0");

  return `${year}-${month}-${day}`;
}

function normalizeTimeForUrl(time) {
  if (!time) return "";
  return String(time).slice(0, 5);
}

function timeToMinutes(time) {
  const cleanTime = normalizeTimeForUrl(time);
  const [hours, minutes] = cleanTime.split(":").map(Number);

  if (!Number.isFinite(hours) || !Number.isFinite(minutes)) return 0;

  return hours * 60 + minutes;
}

function parseTableIds(value) {
  if (!value) return [];

  return String(value)
    .split(",")
    .map((id) => id.trim())
    .filter(Boolean)
    .map((id) => Number(id))
    .filter((id) => !Number.isNaN(id));
}

function isCancelledReservation(reservation) {
  const status = String(reservation?.status || "").toLowerCase();

  return (
    status === "cancelled" ||
    status === "cancelled_customer" ||
    status === "cancelled_restaurant" ||
    status === "cancelled_by_customer" ||
    status === "cancelled_by_restaurant" ||
    status === "no_show" ||
    status === "completed"
  );
}

function getTableZoneId(table) {
  if (!table) return "unknown";

  if (table.place_of_table?.id) return String(table.place_of_table.id);
  if (table.place_of_table?.pk) return String(table.place_of_table.pk);

  if (typeof table.place_of_table === "number") {
    return String(table.place_of_table);
  }

  if (typeof table.place_of_table === "string") {
    return table.place_of_table;
  }

  if (table.place_of_table_id) return String(table.place_of_table_id);

  return "unknown";
}

function getTableZoneName(table) {
  if (!table) return "";

  if (table.place_of_table?.place_of_table) {
    return table.place_of_table.place_of_table;
  }

  if (table.place_of_table?.name) {
    return table.place_of_table.name;
  }

  if (typeof table.place_of_table === "string") {
    return table.place_of_table;
  }

  return "";
}

function getReservationZoneId(reservation) {
  const zone = reservation?.table_place_preference;

  if (!zone) return "";

  if (zone.id) return String(zone.id);
  if (zone.pk) return String(zone.pk);

  if (typeof zone === "number") return String(zone);

  return "";
}

function getReservationZoneName(reservation) {
  const zone = reservation?.table_place_preference;

  if (!zone) return "";

  if (zone.place_of_table) return zone.place_of_table;
  if (zone.name) return zone.name;

  if (typeof zone === "string") return zone;

  return "";
}

function getReservationPrimaryZoneId(reservation, allTables) {
  const tableIds = parseTableIds(reservation?.tablesused);

  const firstTable = (allTables || []).find((table) =>
    tableIds.includes(Number(table.id)),
  );

  if (firstTable) return getTableZoneId(firstTable);

  return getReservationZoneId(reservation) || null;
}

function reservationUsesZone(reservation, zoneId, allTables) {
  if (!reservation || !zoneId) return true;

  const tableIds = parseTableIds(reservation.tablesused);

  if (!tableIds.length) {
    return (
      String(getReservationPrimaryZoneId(reservation, allTables)) ===
      String(zoneId)
    );
  }

  return (allTables || []).some((table) => {
    return (
      tableIds.includes(Number(table.id)) &&
      String(getTableZoneId(table)) === String(zoneId)
    );
  });
}

function buildZoneOptions(allTables, reservations) {
  const zoneMap = new Map();

  (allTables || []).forEach((table) => {
    const zoneId = getTableZoneId(table);
    const tableZoneName = getTableZoneName(table);

    if (!zoneMap.has(zoneId)) {
      zoneMap.set(zoneId, {
        id: zoneId,
        label: tableZoneName || "",
      });
    } else if (tableZoneName && !zoneMap.get(zoneId).label) {
      zoneMap.set(zoneId, {
        ...zoneMap.get(zoneId),
        label: tableZoneName,
      });
    }
  });

  (reservations || []).forEach((reservation) => {
    const reservationZoneId = getReservationZoneId(reservation);
    const reservationZoneName = getReservationZoneName(reservation);

    if (reservationZoneId && reservationZoneName) {
      zoneMap.set(reservationZoneId, {
        id: reservationZoneId,
        label: reservationZoneName,
      });
    }
  });

  return Array.from(zoneMap.values()).map((zone, index) => ({
    id: zone.id,
    label: zone.label || `Sala ${index + 1}`,
  }));
}

function parseDateTime(dateValue, timeValue) {
  const date = normalizeDateForUrl(dateValue);
  const time = normalizeTimeForUrl(timeValue);

  if (!date || !time) return null;

  const parsed = new Date(`${date}T${time}:00`);

  if (Number.isNaN(parsed.getTime())) return null;

  return parsed;
}

function getReservationEndDateTime(reservation) {
  const start = parseDateTime(reservation?.date, reservation?.time);

  if (!start) return null;

  if (reservation?.estimatedtime) {
    const end = parseDateTime(reservation.date, reservation.estimatedtime);

    if (end && end > start) return end;

    if (end && end <= start) {
      const nextDayEnd = new Date(end);
      nextDayEnd.setDate(nextDayEnd.getDate() + 1);
      return nextDayEnd;
    }
  }

  const fallback = new Date(start);
  fallback.setHours(fallback.getHours() + 2);
  return fallback;
}

function getReservationTimeRange(reservation) {
  const start = parseDateTime(reservation?.date, reservation?.time);
  const end = getReservationEndDateTime(reservation);

  if (!start || !end) {
    return {
      start: null,
      end: null,
    };
  }

  return {
    start,
    end,
  };
}

function reservationIsActiveAtDateTime(reservation, targetDateTime) {
  if (!reservation || !targetDateTime) return false;

  const range = getReservationTimeRange(reservation);

  if (!range.start || !range.end) return false;

  return range.start <= targetDateTime && range.end > targetDateTime;
}

function buildOccupiedByTableId(reservationsAtTime) {
  const map = new Map();

  (reservationsAtTime || []).forEach((reservation) => {
    if (isCancelledReservation(reservation)) return;

    const tableIds = parseTableIds(reservation.tablesused);

    tableIds.forEach((tableId) => {
      const key = String(tableId);
      const existing = map.get(key) || [];

      existing.push({
        id: reservation.id,
        full_name: reservation.full_name,
        time: reservation.time,
        estimatedtime: reservation.estimatedtime,
        number_of_people: reservation.number_of_people,
      });

      map.set(key, existing);
    });
  });

  return map;
}

function buildAvailabilityTablesForZone(zoneTables, occupiedByTableId) {
  return (zoneTables || [])
    .sort((a, b) => Number(a.table_number) - Number(b.table_number))
    .map((table) => {
      const occupiedReservations =
        occupiedByTableId.get(String(table.id)) || [];

      return {
        number: String(table.id),
        status: occupiedReservations.length > 0 ? "1" : "0",
        reservationIds: occupiedReservations.map((reservation) =>
          String(reservation.id),
        ),
        reservationDetails: occupiedReservations,
      };
    });
}

function buildSelectedTablesForReservation(reservation, zoneName) {
  const tableIds = parseTableIds(reservation?.tablesused);

  if (!reservation || !tableIds.length) return {};

  return {
    [zoneName]: tableIds.map((tableId) => ({
      number: String(tableId),
      status: "1",
      reservationIds: [String(reservation.id)],
      reservationDetails: [
        {
          id: reservation.id,
          full_name: reservation.full_name,
          time: reservation.time,
          estimatedtime: reservation.estimatedtime,
          number_of_people: reservation.number_of_people,
        },
      ],
    })),
  };
}

function buildUniqueReservationStartTimes(reservationsForRoom) {
  return Array.from(
    new Set(
      (reservationsForRoom || [])
        .map((reservation) => normalizeTimeForUrl(reservation.time))
        .filter(Boolean),
    ),
  ).sort((a, b) => timeToMinutes(a) - timeToMinutes(b));
}

function getLayoutReservationId(layoutItem) {
  return String(
    layoutItem?.reservation_id ??
      layoutItem?.reservationId ??
      layoutItem?.reservation ??
      "",
  );
}

function getLayoutTableId(layoutItem) {
  return String(
    layoutItem?.table_id ?? layoutItem?.tableId ?? layoutItem?.table ?? "",
  );
}

function getLayoutKey(layoutItem) {
  return `${getLayoutReservationId(layoutItem)}:${getLayoutTableId(layoutItem)}`;
}

function mergeLayoutItems(existingItems = [], savedItems = []) {
  const map = new Map();

  existingItems.forEach((item) => {
    const key = getLayoutKey(item);
    if (key !== ":") map.set(key, item);
  });

  savedItems.forEach((item) => {
    const key = getLayoutKey(item);
    if (key !== ":") map.set(key, item);
  });

  return Array.from(map.values());
}

function mergeReservationsById(reservations = [], floorReservations = []) {
  const mergedMap = new Map();

  (reservations || []).forEach((reservation) => {
    if (!reservation?.id) return;
    mergedMap.set(String(reservation.id), reservation);
  });

  (floorReservations || []).forEach((reservation) => {
    if (!reservation?.id) return;

    const id = String(reservation.id);
    const existingReservation = mergedMap.get(id) || {};

    mergedMap.set(id, {
      ...existingReservation,
      ...reservation,
    });
  });

  return Array.from(mergedMap.values());
}

export default function DailyFloorBoard({
  reservations = [],
  floorReservations = [],
  allTables = [],
  restaurantId,
  restaurantTimezone = "Europe/London",
  dateFilter,
  workspaceHeight,
  leftControls = null,
  dispatch_reservation_wait = () => {},
  past_estimated_time = false,
  reservationLayoutSocketUpdate = null,
}) {
  const [selectedReservationId, setSelectedReservationId] = useState(null);
  const [activeZoneId, setActiveZoneId] = useState(null);
  const [selectedFloorTime, setSelectedFloorTime] = useState(null);

  const [reservationLayoutOverrides, setReservationLayoutOverrides] = useState(
    [],
  );
  const [reservationLayoutDraftItems, setReservationLayoutDraftItems] =
    useState([]);
  const [floorToolMode, setFloorToolMode] = useState("select");
  const [isSavingLayout, setIsSavingLayout] = useState(false);
  const [, setIsLoadingLayout] = useState(false);

  const [floorSnapshot, setFloorSnapshot] = useState(null);
  const [isFloorSwitching, setIsFloorSwitching] = useState(false);

  const floorLoadTimerRef = useRef(null);
  const floorLoadRequestIdRef = useRef(0);

  const visibleReservations = useMemo(() => {
    return (reservations || [])
      .filter((reservation) => !isCancelledReservation(reservation))
      .sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")));
  }, [reservations]);

  const visibleFloorReservations = useMemo(() => {
    return mergeReservationsById(reservations, floorReservations)
      .filter((reservation) => !isCancelledReservation(reservation))
      .sort((a, b) => String(a.time || "").localeCompare(String(b.time || "")));
  }, [floorReservations, reservations]);

  const zoneOptions = useMemo(
    () => buildZoneOptions(allTables, visibleFloorReservations),
    [allTables, visibleFloorReservations],
  );

  const zoneOptionsKey = useMemo(
    () => zoneOptions.map((zone) => `${zone.id}:${zone.label}`).join("|"),
    [zoneOptions],
  );

  const effectiveActiveZoneId = activeZoneId || zoneOptions[0]?.id || null;

  const selectedReservation = useMemo(() => {
    const selectedFromFloorList = visibleFloorReservations.find(
      (reservation) => Number(reservation.id) === Number(selectedReservationId),
    );

    if (selectedFromFloorList) return selectedFromFloorList;

    const selectedFromVisibleList = visibleReservations.find(
      (reservation) => Number(reservation.id) === Number(selectedReservationId),
    );

    if (selectedFromVisibleList) return selectedFromVisibleList;

    return visibleFloorReservations[0] || visibleReservations[0] || null;
  }, [visibleReservations, visibleFloorReservations, selectedReservationId]);

  useEffect(() => {
    if (!zoneOptions.length) {
      setActiveZoneId(null);
      return;
    }

    setActiveZoneId((currentZoneId) => {
      const currentStillExists = zoneOptions.some(
        (zone) => String(zone.id) === String(currentZoneId),
      );

      if (currentZoneId && currentStillExists) return currentZoneId;

      const reservationZoneId = selectedReservation
        ? getReservationPrimaryZoneId(selectedReservation, allTables)
        : null;

      return reservationZoneId || zoneOptions[0]?.id || null;
    });
  }, [zoneOptionsKey, selectedReservation?.id, allTables, zoneOptions]);

  useEffect(() => {
    if (!selectedReservation?.id) {
      setSelectedReservationId(null);
      return;
    }

    setSelectedReservationId((currentId) => {
      const currentStillExists = visibleFloorReservations.some(
        (reservation) => Number(reservation.id) === Number(currentId),
      );

      if (currentId && currentStillExists) return currentId;

      return selectedReservation.id;
    });
  }, [selectedReservation?.id, visibleFloorReservations]);

  const activeZone = zoneOptions.find(
    (zone) => String(zone.id) === String(effectiveActiveZoneId),
  );

  const activeZoneName = activeZone?.label || zoneOptions[0]?.label || "Planta";

  const activeZoneTables = useMemo(() => {
    if (!effectiveActiveZoneId) return [];

    return (allTables || []).filter(
      (table) =>
        String(getTableZoneId(table)) === String(effectiveActiveZoneId),
    );
  }, [allTables, effectiveActiveZoneId]);

  const roomReservations = useMemo(() => {
    return visibleFloorReservations.filter((reservation) =>
      reservationUsesZone(reservation, effectiveActiveZoneId, allTables),
    );
  }, [visibleFloorReservations, effectiveActiveZoneId, allTables]);

  const floorStartTimes = useMemo(() => {
    return buildUniqueReservationStartTimes(visibleFloorReservations);
  }, [visibleFloorReservations]);

  const floorStartTimesKey = useMemo(
    () => floorStartTimes.join("|"),
    [floorStartTimes],
  );

  useEffect(() => {
    if (!floorStartTimes.length) {
      setSelectedFloorTime(null);
      return;
    }

    setSelectedFloorTime((currentTime) => {
      if (currentTime && floorStartTimes.includes(currentTime)) {
        return currentTime;
      }

      const selectedReservationTime = normalizeTimeForUrl(
        selectedReservation?.time,
      );

      if (
        selectedReservationTime &&
        floorStartTimes.includes(selectedReservationTime)
      ) {
        return selectedReservationTime;
      }

      return floorStartTimes[0] || null;
    });
  }, [floorStartTimesKey, selectedReservation?.id, selectedReservation?.time]);

  const floorDate = normalizeDateForUrl(
    selectedReservation?.date ||
      visibleFloorReservations[0]?.date ||
      dateFilter,
  );

  const selectedFloorDateTime = useMemo(() => {
    if (!floorDate || !selectedFloorTime) return null;

    return parseDateTime(floorDate, selectedFloorTime);
  }, [floorDate, selectedFloorTime]);

  const activeFloorReservations = useMemo(() => {
    if (!selectedFloorDateTime) {
      return selectedReservation ? [selectedReservation] : [];
    }

    return visibleFloorReservations.filter((reservation) =>
      reservationIsActiveAtDateTime(reservation, selectedFloorDateTime),
    );
  }, [visibleFloorReservations, selectedFloorDateTime, selectedReservation]);

  const activeRoomFloorReservations = useMemo(() => {
    return activeFloorReservations.filter((reservation) =>
      reservationUsesZone(reservation, effectiveActiveZoneId, allTables),
    );
  }, [activeFloorReservations, effectiveActiveZoneId, allTables]);

  const layoutContextReservation = useMemo(() => {
    if (!activeRoomFloorReservations.length) return selectedReservation;

    const selectedStillActive = activeRoomFloorReservations.find(
      (reservation) =>
        Number(reservation.id) === Number(selectedReservation?.id),
    );

    return selectedStillActive || activeRoomFloorReservations[0];
  }, [activeRoomFloorReservations, selectedReservation]);

  const occupiedByTableId = useMemo(
    () => buildOccupiedByTableId(activeFloorReservations),
    [activeFloorReservations],
  );

  const availabilityTables = useMemo(
    () => buildAvailabilityTablesForZone(activeZoneTables, occupiedByTableId),
    [activeZoneTables, occupiedByTableId],
  );

  const selectedReservationIsActiveAtSelectedTime = useMemo(() => {
    if (!selectedReservation) return false;
    if (!selectedFloorDateTime) return true;

    return reservationIsActiveAtDateTime(
      selectedReservation,
      selectedFloorDateTime,
    );
  }, [selectedReservation, selectedFloorDateTime]);

  const selectedTables = useMemo(() => {
    if (!selectedReservationIsActiveAtSelectedTime) return {};

    return buildSelectedTablesForReservation(
      selectedReservation,
      activeZoneName,
    );
  }, [
    selectedReservation,
    activeZoneName,
    selectedReservationIsActiveAtSelectedTime,
  ]);

  const floorContextKey = useMemo(() => {
    return [
      restaurantId || "",
      layoutContextReservation?.id || "no-reservation",
      effectiveActiveZoneId || "no-zone",
      selectedFloorTime || "no-time",
      activeZoneName || "no-zone-name",
    ].join("|");
  }, [
    restaurantId,
    layoutContextReservation?.id,
    effectiveActiveZoneId,
    selectedFloorTime,
    activeZoneName,
  ]);

  const handleReservationSelect = (reservation, options = {}) => {
    if (!reservation?.id) return;

    const { keepCurrentFloorTime = false, keepCurrentZone = false } = options;

    setSelectedReservationId(reservation.id);

    if (!keepCurrentFloorTime) {
      setSelectedFloorTime(normalizeTimeForUrl(reservation.time));
    }

    if (!keepCurrentZone) {
      const zoneId = getReservationPrimaryZoneId(reservation, allTables);

      if (zoneId) setActiveZoneId(zoneId);
    }
  };

  const handleFloorTimeSelect = (time) => {
    const cleanTime = normalizeTimeForUrl(time);

    setSelectedFloorTime(cleanTime);

    const targetDateTime = floorDate
      ? parseDateTime(floorDate, cleanTime)
      : null;

    const startingReservationInCurrentRoom =
      roomReservations.find(
        (reservation) => normalizeTimeForUrl(reservation.time) === cleanTime,
      ) || null;

    if (startingReservationInCurrentRoom) {
      handleReservationSelect(startingReservationInCurrentRoom, {
        keepCurrentFloorTime: true,
      });

      return;
    }

    const activeReservationInCurrentRoom = targetDateTime
      ? roomReservations.find((reservation) =>
          reservationIsActiveAtDateTime(reservation, targetDateTime),
        )
      : null;

    if (activeReservationInCurrentRoom) {
      handleReservationSelect(activeReservationInCurrentRoom, {
        keepCurrentFloorTime: true,
        keepCurrentZone: true,
      });

      return;
    }

    const startingReservationInAnyRoom =
      visibleFloorReservations.find(
        (reservation) => normalizeTimeForUrl(reservation.time) === cleanTime,
      ) ||
      visibleReservations.find(
        (reservation) => normalizeTimeForUrl(reservation.time) === cleanTime,
      ) ||
      null;

    if (startingReservationInAnyRoom) {
      handleReservationSelect(startingReservationInAnyRoom, {
        keepCurrentFloorTime: true,
      });

      return;
    }

    const activeReservationInAnyRoom = targetDateTime
      ? visibleFloorReservations.find((reservation) =>
          reservationIsActiveAtDateTime(reservation, targetDateTime),
        )
      : null;

    if (!activeReservationInAnyRoom) return;

    handleReservationSelect(activeReservationInAnyRoom, {
      keepCurrentFloorTime: true,
    });
  };

  const handleFloorTableSelect = (tableInfo) => {
    const reservationDetails = tableInfo?.reservationDetails || [];

    if (!reservationDetails.length) return;

    const nextReservation =
      reservationDetails.find(
        (reservation) =>
          Number(reservation.id) !== Number(selectedReservation?.id),
      ) || reservationDetails[0];

    if (!nextReservation?.id) return;

    const fullReservation =
      visibleFloorReservations.find(
        (reservation) => Number(reservation.id) === Number(nextReservation.id),
      ) ||
      visibleReservations.find(
        (reservation) => Number(reservation.id) === Number(nextReservation.id),
      );

    if (!fullReservation) return;

    // Clicking an occupied table should select that reservation
    // AND jump the floor time to that reservation's start time.
    handleReservationSelect(fullReservation);
  };

  useEffect(() => {
    if (!reservationLayoutSocketUpdate?.reservationId) return;
    if (!selectedReservation?.id) return;

    const updatedReservationId = String(
      reservationLayoutSocketUpdate.reservationId,
    );

    const incomingItems = Array.isArray(
      reservationLayoutSocketUpdate.layoutItems,
    )
      ? reservationLayoutSocketUpdate.layoutItems
      : [];

    setFloorSnapshot((previousSnapshot) => {
      if (!previousSnapshot) return previousSnapshot;

      const previousWithoutUpdatedReservation =
        previousSnapshot.layoutOverrides.filter(
          (item) => getLayoutReservationId(item) !== updatedReservationId,
        );

      const normalizedIncomingItems = incomingItems.map((item) => ({
        ...item,
        reservation_id:
          item.reservation_id ?? item.reservationId ?? updatedReservationId,
      }));

      return {
        ...previousSnapshot,
        layoutOverrides: [
          ...previousWithoutUpdatedReservation,
          ...normalizedIncomingItems,
        ],
      };
    });
  }, [reservationLayoutSocketUpdate, selectedReservation?.id]);

  useEffect(() => {
    const requestId = floorLoadRequestIdRef.current + 1;
    floorLoadRequestIdRef.current = requestId;

    if (floorLoadTimerRef.current) {
      clearTimeout(floorLoadTimerRef.current);
    }

    setIsFloorSwitching(true);
    setReservationLayoutDraftItems([]);

    const makeSnapshot = (layoutOverridesForSnapshot = []) => ({
      key: floorContextKey,
      activeZoneName,
      availabilityTables,
      selectedTables,
      layoutOverrides: layoutOverridesForSnapshot,
    });

    floorLoadTimerRef.current = setTimeout(async () => {
      if (!restaurantId || !layoutContextReservation?.id) {
        if (requestId !== floorLoadRequestIdRef.current) return;

        setReservationLayoutOverrides([]);
        setFloorSnapshot(makeSnapshot([]));

        requestAnimationFrame(() => {
          if (requestId === floorLoadRequestIdRef.current) {
            setIsFloorSwitching(false);
          }
        });

        return;
      }

      try {
        setIsLoadingLayout(true);

        const response = await api.get(
          `restaurants/${restaurantId}/reservation-table-layout/${layoutContextReservation.id}/`,
        );

        if (requestId !== floorLoadRequestIdRef.current) return;

        const responseData = Array.isArray(response.data) ? response.data : [];

        setReservationLayoutOverrides(responseData);
        setFloorSnapshot(makeSnapshot(responseData));
      } catch (error) {
        if (requestId !== floorLoadRequestIdRef.current) return;

        console.error("Failed to load reservation floor layout:", error);

        setReservationLayoutOverrides([]);
        setFloorSnapshot(makeSnapshot([]));
      } finally {
        if (requestId === floorLoadRequestIdRef.current) {
          setIsLoadingLayout(false);

          requestAnimationFrame(() => {
            if (requestId === floorLoadRequestIdRef.current) {
              setIsFloorSwitching(false);
            }
          });
        }
      }
    }, 120);

    return () => {
      if (floorLoadTimerRef.current) {
        clearTimeout(floorLoadTimerRef.current);
      }
    };
  }, [
    floorContextKey,
    restaurantId,
    layoutContextReservation?.id,
    activeZoneName,
    availabilityTables,
    selectedTables,
    setIsLoadingLayout,
  ]);

  const handleReservationLayoutItemChange = (layoutItem) => {
    setReservationLayoutDraftItems((prevItems) => {
      const nextItems = [...prevItems];

      const existingIndex = nextItems.findIndex(
        (item) => String(item.table_id) === String(layoutItem.table_id),
      );

      if (existingIndex >= 0) {
        nextItems[existingIndex] = {
          ...nextItems[existingIndex],
          ...layoutItem,
        };
      } else {
        nextItems.push(layoutItem);
      }

      return nextItems;
    });
  };

  const discardReservationLayoutDraft = () => {
    setReservationLayoutDraftItems([]);
    setFloorToolMode("select");
  };

  const saveReservationLayoutDraft = async () => {
    if (!reservationLayoutDraftItems.length) return;

    if (!restaurantId || !layoutContextReservation?.id) {
      console.error(
        "Missing restaurant/reservation for saving daily floor layout.",
      );
      return;
    }

    try {
      setIsSavingLayout(true);

      const cleanLayoutItems = reservationLayoutDraftItems
        .filter((item) => item && item.table_id)
        .map((item) => ({
          table_id: item.table_id,
          left: Number(item.left || 0),
          top: Number(item.top || 0),
          width: Number(item.width || 50),
          height: Number(item.height || 50),
          isPlaced: item.isPlaced !== false,
          rotation: Number(item.rotation || 0),
        }));

      const response = await api.patch(
        `restaurants/${restaurantId}/reservation-table-layout/${layoutContextReservation.id}/`,
        {
          items: cleanLayoutItems,
        },
      );

      const savedItems = Array.isArray(response.data?.layout_items)
        ? response.data.layout_items
        : Array.isArray(response.data?.items)
          ? response.data.items
          : [];

      setReservationLayoutOverrides((prevItems) =>
        mergeLayoutItems(prevItems, savedItems),
      );

      setFloorSnapshot((previousSnapshot) => {
        if (!previousSnapshot) return previousSnapshot;

        return {
          ...previousSnapshot,
          layoutOverrides: mergeLayoutItems(
            previousSnapshot.layoutOverrides,
            savedItems,
          ),
        };
      });

      setReservationLayoutDraftItems([]);
      setFloorToolMode("select");
    } catch (error) {
      console.error(
        "Failed to save daily floor layout:",
        error.response?.data || error,
      );
    } finally {
      setIsSavingLayout(false);
    }
  };

  const displayedFloor = floorSnapshot || {
    activeZoneName,
    availabilityTables,
    selectedTables,
    layoutOverrides: reservationLayoutOverrides,
  };

  return (
    <BoardWrap $workspaceHeight={workspaceHeight}>
      <LeftPanel>
        {leftControls && <LeftControls>{leftControls}</LeftControls>}

        <ReservationList>
          {visibleReservations.length > 0 ? (
            <ReservationCard
              reservations={visibleReservations}
              reservations_complete={reservations}
              isReservationFromRestaurantScreen={true}
              dispatch_reservation_wait={dispatch_reservation_wait}
              past_estimated_time={past_estimated_time}
              variant="floorBoard"
              selectedReservationId={selectedReservation?.id}
              onReservationSelect={handleReservationSelect}
            />
          ) : (
            <EmptyState>Sem reservas para os filtros selecionados.</EmptyState>
          )}
        </ReservationList>
      </LeftPanel>

      <RightPanel>
        <FloorHeader>
          <FloorHeaderSpacer />

          <FloorHeaderTimeCenter>
            <ReservationTimeCenterScroll
              times={floorStartTimes}
              value={selectedFloorTime}
              onChange={handleFloorTimeSelect}
              restaurantTimezone={restaurantTimezone}
            />
          </FloorHeaderTimeCenter>

          <FloorHeaderActions>
            <FloorToolButton
              type="button"
              active={floorToolMode === "select"}
              onClick={() => setFloorToolMode("select")}
              disabled={isSavingLayout}
            >
              Selecionar
            </FloorToolButton>

            <FloorToolButton
              type="button"
              active={floorToolMode === "move"}
              onClick={() => setFloorToolMode("move")}
              disabled={isSavingLayout || !layoutContextReservation?.id}
              title={
                layoutContextReservation?.id
                  ? "Mover mesas desta reserva"
                  : "Só é possível guardar posições quando existe uma reserva selecionada"
              }
            >
              Mover
            </FloorToolButton>

            {reservationLayoutDraftItems.length > 0 && (
              <>
                <FloorToolButton
                  type="button"
                  onClick={discardReservationLayoutDraft}
                  disabled={isSavingLayout}
                >
                  Descartar
                </FloorToolButton>

                <FloorToolButton
                  type="button"
                  active
                  onClick={saveReservationLayoutDraft}
                  disabled={isSavingLayout || !layoutContextReservation?.id}
                >
                  {isSavingLayout
                    ? "A guardar…"
                    : `Guardar ${reservationLayoutDraftItems.length}`}
                </FloorToolButton>
              </>
            )}
          </FloorHeaderActions>
        </FloorHeader>

        {zoneOptions.length > 1 && (
          <ZonePickerBox>
            <ZonePickerLabel>Sala</ZonePickerLabel>

            {zoneOptions.length <= 4 ? (
              zoneOptions.map((zone) => (
                <ZoneBubble
                  key={zone.id}
                  type="button"
                  active={String(zone.id) === String(effectiveActiveZoneId)}
                  onClick={() => setActiveZoneId(zone.id)}
                >
                  {zone.label}
                </ZoneBubble>
              ))
            ) : (
              <ZoneSelect
                value={effectiveActiveZoneId || ""}
                onChange={(event) => setActiveZoneId(event.target.value)}
              >
                {zoneOptions.map((zone) => (
                  <option key={zone.id} value={zone.id}>
                    {zone.label}
                  </option>
                ))}
              </ZoneSelect>
            )}
          </ZonePickerBox>
        )}

        <FloorPlanArea $isSwitching={isFloorSwitching}>
          {zoneOptions.length > 0 ? (
            <>
              <ReservationFloorPlan
                allTables={allTables}
                zoneName={displayedFloor.activeZoneName}
                availabilityTables={displayedFloor.availabilityTables}
                selectedTables={displayedFloor.selectedTables}
                onSelectTable={handleFloorTableSelect}
                layoutOverrides={displayedFloor.layoutOverrides}
                layoutDraftItems={reservationLayoutDraftItems}
                enableMove={
                  floorToolMode === "move" &&
                  !!layoutContextReservation?.id &&
                  !isFloorSwitching
                }
                onLayoutItemChange={handleReservationLayoutItemChange}
              />

              <Legend>
                <span>
                  <LegendDot color="#F8F6F2" />
                  Livre
                </span>

                <span>
                  <LegendDot color="#99B0AD" />
                  Ocupada
                </span>

                <span>
                  <LegendDot color="#EE561B" />
                  Selecionada
                </span>
              </Legend>
            </>
          ) : (
            <EmptyState>Sem salas ou mesas configuradas.</EmptyState>
          )}
        </FloorPlanArea>
      </RightPanel>
    </BoardWrap>
  );
}
